import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosInstance';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { AlertTriangle, ShieldAlert, Mail } from 'lucide-react';

interface EligibilityItem {
  code: string;
  message: string;
}

interface Eligibility {
  eligible: boolean;
  blockers: EligibilityItem[];
  warnings: EligibilityItem[];
  gracePeriodDays: number;
}

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  username: string;
  /** Called after the deletion is confirmed — must log the user out. */
  onDeleted: () => void;
}

type Step = 'loading' | 'blocked' | 'consequences' | 'verify' | 'otp';

/**
 * Multi-step self-service account deletion:
 *   1. consequences — eligibility check + what will happen
 *   2. verify       — current password + type-username-to-confirm
 *   3. otp          — email verification code + optional reason
 */
export default function DeleteAccountDialog({ open, onOpenChange, username, onDeleted }: DeleteAccountDialogProps) {
  const [step, setStep] = useState<Step>('loading');
  const [eligibility, setEligibility] = useState<Eligibility | null>(null);
  const [password, setPassword] = useState('');
  const [confirmUsername, setConfirmUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setStep('loading');
    setEligibility(null);
    setPassword('');
    setConfirmUsername('');
    setOtp('');
    setReason('');
    setSubmitting(false);
    setError('');
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/account/deletion-eligibility');
        if (cancelled) return;
        setEligibility(res.data);
        setStep(res.data.eligible ? 'consequences' : 'blocked');
      } catch {
        if (cancelled) return;
        setError('Failed to check eligibility. Please try again.');
        setStep('blocked');
      }
    })();
    return () => { cancelled = true; };
  }, [open, reset]);

  const handleClose = (next: boolean) => {
    if (submitting) return;
    onOpenChange(next);
  };

  const handleInitiate = async () => {
    setSubmitting(true);
    setError('');
    try {
      await axios.post('/api/account/delete/initiate', { password });
      toast.success('Verification code sent to your email');
      setStep('otp');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post('/api/account/delete/confirm', {
        otp: otp.trim(),
        reason: reason.trim() || undefined,
      });
      toast.success('Your account is scheduled for deletion. Check your email for details.');
      if (res.data.forceLogout) onDeleted();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete account');
      setSubmitting(false);
    }
  };

  const graceDays = eligibility?.gracePeriodDays ?? 30;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="w-5 h-5" /> Delete my account
          </DialogTitle>
          <DialogDescription>
            This permanently deletes your account and content after a {graceDays}-day grace period.
          </DialogDescription>
        </DialogHeader>

        {step === 'loading' && (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        )}

        {step === 'blocked' && (
          <div className="space-y-3">
            {error && <p className="text-sm text-red-600">{error}</p>}
            {eligibility?.blockers.map(b => (
              <div key={b.code} className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-destructive" />
                <p className="text-sm text-foreground">{b.message}</p>
              </div>
            ))}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => handleClose(false)}>Close</Button>
            </div>
          </div>
        )}

        {step === 'consequences' && (
          <div className="space-y-4">
            <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
              <li>Your public website goes <strong className="text-foreground">offline immediately</strong>.</li>
              <li>Managers linked to your account lose access immediately and are notified by email.</li>
              <li>Active subscriptions are cancelled (no automatic refund).</li>
              <li>
                After <strong className="text-foreground">{graceDays} days</strong>, your account, website content,
                and images are <strong className="text-foreground">permanently erased</strong>. This cannot be undone.
              </li>
              <li>Billing records and invoices are retained as required by law.</li>
              <li>You can cancel by logging in any time within the {graceDays}-day grace period.</li>
            </ul>
            {eligibility && eligibility.warnings.length > 0 && (
              <div className="space-y-2">
                {eligibility.warnings.map(w => (
                  <div key={w.code} className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-yellow-600" />
                    <p className="text-sm text-foreground">{w.message}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => setStep('verify')}>Continue</Button>
            </div>
          </div>
        )}

        {step === 'verify' && (
          <form
            className="space-y-4"
            onSubmit={e => { e.preventDefault(); handleInitiate(); }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="delete-password">Current password</Label>
              <PasswordInput
                id="delete-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your current password"
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="delete-username">
                Type <code className="px-1 py-0.5 rounded bg-muted font-mono text-xs">{username}</code> to confirm
              </Label>
              <Input
                id="delete-username"
                value={confirmUsername}
                onChange={e => setConfirmUsername(e.target.value)}
                placeholder={username}
                autoComplete="off"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setStep('consequences')} disabled={submitting}>
                Back
              </Button>
              <Button
                type="submit"
                variant="destructive"
                loading={submitting}
                disabled={!password || confirmUsername !== username}
              >
                <Mail className="w-4 h-4 mr-1" /> Send verification code
              </Button>
            </div>
          </form>
        )}

        {step === 'otp' && (
          <form
            className="space-y-4"
            onSubmit={e => { e.preventDefault(); handleConfirm(); }}
          >
            <p className="text-sm text-muted-foreground">
              We emailed a 6-digit code to your registered address. Enter it below to confirm the deletion.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="delete-otp">Verification code</Label>
              <Input
                id="delete-otp"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="font-mono tracking-widest"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="delete-reason">Why are you leaving? <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea
                id="delete-reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Your feedback helps us improve"
                maxLength={500}
                rows={2}
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setStep('verify')} disabled={submitting}>
                Back
              </Button>
              <Button type="submit" variant="destructive" loading={submitting} disabled={otp.length !== 6}>
                Delete my account permanently
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
